import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  IconComponent,
  InputComponent,
  TooltipComponent,
  TypographyComponent,
} from '@purolator/ui-kit-angular';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import { EMOJI_VALIDATION_ERROR_KEYS } from '../../../../shared/validators/emoji.validators';
import {
  FormErrorSummary,
  type FormErrorSummaryItem,
} from '../../../../shared/components/form-error-summary/form-error-summary';
import { isPackageDescriptionRequired } from '../../forms/additional-coverage.form';
import { DECLARED_VALUE_VALIDATION_ERROR_KEYS } from '../../validators/declared-value.validators';

/**
 * Service-step Additional Coverage accordion (Declared Value + package description).
 * Visual layout follows Figma node 18972:23628.
 */
@Component({
  selector: 'app-additional-coverage-section',
  imports: [
    ReactiveFormsModule,
    TypographyComponent,
    InputComponent,
    IconComponent,
    TooltipComponent,
    TranslatePipe,
    FormErrorSummary,
  ],
  templateUrl: './additional-coverage-section.html',
  styleUrl: './additional-coverage-section.scss',
})
export class AdditionalCoverageSection {
  private readonly translationService = inject(TranslationService);

  /** Reactive form owned by the Service step page. */
  readonly form = input.required<
    FormGroup<{
      declaredValue: FormControl<string>;
      packageDescription: FormControl<string>;
    }>
  >();

  /** When true, field-level validation messages are shown. */
  readonly showValidationErrors = input(false);

  /**
   * Emits the declared-value control value when the user leaves the field after a change.
   * Tabbing through without editing does not emit.
   */
  readonly declaredValueCommitted = output<string>();

  /** Accordion starts expanded to match the Figma default state. */
  protected readonly expanded = signal(true);

  /**
   * Bumps when declared value changes so the required asterisk recomputes during CD.
   */
  protected readonly declaredValueRevision = signal(0);

  /** Bumps on any form value change so the error banner stays in sync. */
  private readonly formRevision = signal(0);

  /** Last declared value emitted (or seeded from the form) so unchanged blurs are ignored. */
  private lastCommittedDeclaredValue: string | null = null;

  constructor() {
    effect((onCleanup) => {
      const form = this.form();
      this.lastCommittedDeclaredValue = form.controls.declaredValue.value;
      const subscription = form.valueChanges.subscribe(() => {
        this.onDeclaredValueChanged();
        this.formRevision.update((n) => n + 1);
      });
      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => {
      if (this.showValidationErrors()) {
        this.expanded.set(true);
      }
    });
  }

  /**
   * Builds Package-style summary banner items for invalid coverage fields.
   * Includes a field after blur/TAB (touched) or after Save (showValidationErrors).
   */
  protected readonly errorSummaryMessages = computed((): FormErrorSummaryItem[] => {
    this.translationService.revision();
    this.formRevision();
    this.declaredValueRevision();
    this.showValidationErrors();

    const form = this.form();
    const messages: FormErrorSummaryItem[] = [];
    const declared = form.controls.declaredValue;
    const description = form.controls.packageDescription;
    const showDeclared = declared.invalid && (declared.touched || this.showValidationErrors());
    const showDescription =
      description.invalid && (description.touched || this.showValidationErrors());

    if (showDeclared) {
      if (declared.hasError(DECLARED_VALUE_VALIDATION_ERROR_KEYS.aboveMax)) {
        messages.push({
          emphasis: this.translationService.translate(
            'createShipment.serviceStep.additionalCoverage.declaredValueLabel',
          ),
          detail: this.translationService.translate(
            'createShipment.serviceStep.additionalCoverage.errors.aboveMax',
          ),
        });
      } else if (declared.hasError(DECLARED_VALUE_VALIDATION_ERROR_KEYS.invalid)) {
        messages.push({
          emphasis: this.translationService.translate(
            'createShipment.serviceStep.additionalCoverage.declaredValueLabel',
          ),
          detail: this.translationService.translate(
            'createShipment.serviceStep.additionalCoverage.errors.invalid',
          ),
        });
      }
    }

    if (
      showDescription &&
      description.hasError(DECLARED_VALUE_VALIDATION_ERROR_KEYS.descriptionRequired)
    ) {
      messages.push({
        emphasis: this.translationService.translate(
          'createShipment.serviceStep.additionalCoverage.packageDescriptionLabel',
        ),
        detail: this.translationService.translate(
          'createShipment.serviceStep.additionalCoverage.errors.descriptionRequired',
        ),
      });
    } else if (
      showDescription &&
      description.hasError(EMOJI_VALIDATION_ERROR_KEYS.restrictedCharacters)
    ) {
      messages.push({
        emphasis: this.translationService.translate(
          'createShipment.serviceStep.additionalCoverage.packageDescriptionLabel',
        ),
        detail: this.translationService.translate(
          'createShipment.validation.restrictedCharactersField',
        ),
      });
    }

    return messages;
  });

  /**
   * Toggles the Additional Coverage accordion open/closed.
   */
  protected toggleExpanded(): void {
    this.expanded.update((value) => !value);
  }

  /**
   * Records declared-value edits so the description required indicator stays in sync.
   */
  protected onDeclaredValueChanged(): void {
    this.declaredValueRevision.update((n) => n + 1);
  }

  /**
   * Marks a coverage control touched on TAB/blur so inline errors match Package timing.
   * Expands the accordion when the field is invalid so the banner stays visible.
   * Commits declared value for estimate refresh only when the value is valid, so an
   * above-max or malformed entry does not tear down the delivery matrix / scroll the page.
   */
  protected onFieldBlur(control: FormControl<string>): void {
    control.markAsTouched();
    this.formRevision.update((n) => n + 1);
    if (control.invalid) {
      this.expanded.set(true);
    }

    if (control === this.form().controls.declaredValue) {
      const next = control.value ?? '';
      if (control.valid && next !== this.lastCommittedDeclaredValue) {
        this.lastCommittedDeclaredValue = next;
        this.declaredValueCommitted.emit(next);
      }
    }
  }

  /**
   * Returns whether package description is mandatory for the current declared value.
   */
  protected isDescriptionRequired(): boolean {
    this.declaredValueRevision();
    return isPackageDescriptionRequired(this.form().controls.declaredValue.value);
  }

  /**
   * Returns whether the declared-value control should show an error state.
   * Shows after blur/TAB (touched) or after Save (showValidationErrors).
   */
  protected hasDeclaredValueError(): boolean {
    this.formRevision();
    const control = this.form().controls.declaredValue;
    return control.invalid && (control.touched || this.showValidationErrors());
  }

  /**
   * Resolves the i18n key for the short declared-value field helper (Package-style).
   * Long blocking copy for > $5000 lives in the summary banner only.
   */
  protected declaredValueErrorKey(): string {
    const control = this.form().controls.declaredValue;
    if (
      control.hasError(DECLARED_VALUE_VALIDATION_ERROR_KEYS.aboveMax) ||
      control.hasError(DECLARED_VALUE_VALIDATION_ERROR_KEYS.invalid)
    ) {
      return 'createShipment.serviceStep.additionalCoverage.errors.invalid';
    }
    return '';
  }

  /**
   * Returns whether the package description control should show an error state.
   * Shows after blur/TAB (touched) or after Save (showValidationErrors).
   */
  protected hasPackageDescriptionError(): boolean {
    this.formRevision();
    const control = this.form().controls.packageDescription;
    return control.invalid && (control.touched || this.showValidationErrors());
  }

  /**
   * Resolves the i18n key for the package description field error message.
   */
  protected packageDescriptionErrorKey(): string {
    const control = this.form().controls.packageDescription;
    if (control.hasError(DECLARED_VALUE_VALIDATION_ERROR_KEYS.descriptionRequired)) {
      return 'createShipment.serviceStep.additionalCoverage.errors.descriptionRequired';
    }
    if (control.hasError(EMOJI_VALIDATION_ERROR_KEYS.restrictedCharacters)) {
      return 'createShipment.validation.restrictedCharactersField';
    }
    return '';
  }
}
